<template>
  <div class="appointment-table customer-table table-responsive">
    <table class="table table-bordernone">
      <tbody>
      <!-- Loading Spinner -->
      <tr v-if="loading">
        <td colspan="3">
          <div class="spinner-container">
            <div class="spinner"></div> <!-- Circular Spinner -->
          </div>
        </td>
      </tr>

      <!-- Error State -->
      <tr v-if="error" class="error-container">
        <td colspan="100%">
          <img src="../../../../../assets/vectors/no_complaints.png" alt="Error" class="error-image" />
          <p class="error-message">{{ error }}</p>
        </td>
      </tr>

      <!-- Complaints table content (shows when not loading and no errors) -->
      <tr v-else v-for="(item, index) in filteredComplaints" :key="item.name">
        <!-- Avatar with updated background and text colors -->
        <td>
          <div
              class="avatar rounded-circle me-2"
              :style="{ backgroundColor: '#dc3545', color: '#fff' }"
          >
            <span>{{ getInitials(item.name) }}</span>
          </div>
        </td>

        <td class="td-1">
          <div class="d-flex">
            <div>
              <!-- Updated date-time class for styling -->
              <h6 class="f-14 mb-0">
                {{ item.name }} • <span class="date-time">{{ item.time }}</span>
              </h6>
              <span class="f-light">{{ item.message }}</span>
            </div>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { complaintDataTable } from "@/services/dataService"; // Import complaintDataTable

export default {
  data() {
    return {
      token: localStorage.getItem('token_set'), // Get token from localStorage
      complaints: [], // API data will be stored here
      filteredComplaints: [], // Filtered complaint by status
      currentStatus: "Pending", // Default status to display
      loading: false, // Loading state to manage API calls
      error: null, // Error state
    };
  },
  created() {
    this.fetchComplaintsData(); // Fetch complaint data after login when component is created
  },
  methods: {
    // Fetch complaint data from API
    async fetchComplaintsData() {
      const profileId = localStorage.getItem("profile_id"); // Retrieve profile ID

      if (!profileId) {
        console.error("Profile ID not found in localStorage");
        this.error = 'Profile ID not found';
        return;
      }

      this.loading = true; // Set loading to true while fetching the data
      this.error = null; // Reset error state

      try {
        // Fetch the complaint data from the API
        const response = await complaintDataTable(profileId, this.token);

        if (response.data && response.data.complaints) {
          this.mapApiDataToComplaints(response.data.complaints); // Map the API response to the expected format
          this.filterByStatus(this.currentStatus); // Initialize filtered complaint by current status
        } else {
          this.error = response.data.message || 'Failed to fetch data.';
        }
      } catch (error) {
        console.error("Error fetching complaint data:", error);
        this.error = 'An error occurred while fetching data.';
      } finally {
        this.loading = false; // Reset loading state
      }
    },

    // Map API response to the expected complaint format
    mapApiDataToComplaints(apiComplaints) {
      this.complaints = apiComplaints.map((item) => ({
        name: item.name || "Unknown dealer_visit",
        message: item.complaines, // Map "complaines" to "message"
        time: this.formatTime(item.created_at), // Use formatTime to format the "created_at" field
        status: item.status,
      }));
    },

    // Method to format the timestamp
    formatTime(datetime) {
      const date = new Date(datetime);
      const options = {hour: 'numeric', minute: 'numeric', hour12: true};

      const formattedDate = date.toISOString().split('T')[0]; // Format date as YYYY-MM-DD
      const formattedTime = date
          .toLocaleTimeString("en-US", options)
          .replace(":", "."); // Format time with dots

      return `${formattedDate} | ${formattedTime}`; // Return formatted date and time
    },

    // Method to filter complaint by status and limit to top 5
    filterByStatus(status) {
      this.currentStatus = status;

      // Filter complaint by status and limit to top 5
      this.filteredComplaints = this.complaints
          .filter((item) => item.status === status)
          .slice(0, 5); // Limit the result to the top 5 complaint
    },

    // Method to get the initials from the name
    getInitials(name) {
      const [firstName, lastName] = name.split(" ");
      const firstInitial = firstName ? firstName[0].toUpperCase() : "";
      const lastInitial = lastName ? lastName[0].toUpperCase() : "";
      return `${firstInitial}${lastInitial}`;
    },
  },
};
</script>

<style scoped>
/* Spinner container to center it */
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px; /* Adjust to your layout */
}

/* Spinner styling */
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1); /* Light background */
  border-top: 4px solid #dc3545; /* Spinner color */
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

/* Animation for the spinner */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Avatar styling */
.avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: lighter; /* Light font weight */
  border-radius: 50%; /* Makes the avatar circular */
  text-transform: uppercase;
}

/* Date-time styling */
.date-time {
  font-size: 12px; /* Set font size for date-time */
  color: grey; /* Set color to grey */
}

/* Status chip styling */
.status-chip {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  font-size: 14px;
  font-weight: lighter; /* Light font weight for chip text */
  color: #333; /* Dark color for text */
  text-transform: capitalize;
}

/* Light color conditions for status */
.pending {
  background-color: #dc3545;
  color: #fff;
}

.inprogress {
  background-color: #0d6efd;
  color: #fff;
}

.completed {
  background-color: #198754;
  color: #fff;
}

/* Center content inside the table row */
.error-container {
  text-align: center;
}

/* Results not found image */
.error-image {
  max-width: 180px;
  display: block;
  margin: 0 auto;
}

/* Results not found message */
.error-message {
  font-size: 15px;
  font-weight: bold;
  color: #8282e6;
  padding-top: 10px;
}
</style>
